// IL TUTORIAL DEL POKER CINESE (I-1218; KB `services/qchinesepoker/tutorial-copione.md` §4, disegno `reports/2026-10-26-tavolo-guidato-carte-disegno.md`): il tavolo VERO (CpTavoloLayout, OfcBoard, DrawTray, CpResoconto)
// sullo strato comune del tavolo guidato, con un Motore a copione (`lib/qcp/tutorial.ts`). ZERO rete, nessuno storage (il «gia` visto» e` di `IconaTutorial`), nessun orologio, niente che conti.
// ⛔ MAI `ChinesePokerMatch` ne` `useActiveQcpMatch`: montare la rotta collega un socket (`ChinesePokerMatch.tsx:68-77`). Si monta solo dalla lobby, mai sopra una partita viva (`tools/verify-tutorial.ts`).
// La regia dei tocchi e` quella del tavolo vero, SENZA il trascinamento: si sceglie la carta e poi la riga (`useOfcPlacement`, lo stesso hook della rotta); toccare una carta posata la rimette in mano; spostarla da una riga
// all'altra e riordinare una riga non ci sono (si rimette in mano e si ripiazza). La regia della rotta (:364-456) NON e` stata estratta: il tutorial ne e` un sottoinsieme dichiarato.
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CpResoconto } from '@/components/chinesepoker/CpResoconto';
import { CpAzioni, CpEtichettaPlancia, CpTavoloLayout } from '@/components/chinesepoker/CpTavolo';
import type { PezziTavolo } from '@/components/chinesepoker/CpTavolo';
import { DrawTray } from '@/components/chinesepoker/DrawTray';
import type { TagliaCp } from '@/components/chinesepoker/cartaCp';
import { OfcBoard } from '@/components/chinesepoker/OfcBoard';
import { PlayingCard } from '@/components/chinesepoker/PlayingCard';
import { StratoTutorial } from '@/components/tutorial/StratoTutorial';
import type { PropsTavolo, SchedaStrato, TestiPasso, TestiStrato } from '@/components/tutorial/StratoTutorial';
import { useOfcPlacement } from '@/hooks/useOfcPlacement';
import { cn } from '@/lib/cn';
import type { Card, Row } from '@/lib/qcp/protocol';
import { resocontoDi } from '@/lib/qcp/resoconto';
import { CAPIENZA, COPIONE_CP, MOTORE_CP, chiave } from '@/lib/qcp/tutorial';
import type { BersaglioCp, MossaCp, StatoCp } from '@/lib/qcp/tutorial';

/** Le taglie del tutorial: le carte VERE del tavolo, ma piu` piccole di quelle della partita (lo schermo si divide col coach). */
/** Sotto questa altezza (un telefono da 360×640) le carte scendono di una taglia: il coach comune ne prende 280 px. */
const ALTEZZA_BASSA = 700;
const bassa = (): boolean => window.innerHeight < ALTEZZA_BASSA;
/** Sotto i 700 px la MIA plancia scende a `mini` (28×40, la misura che il tavolo usa gia` per la plancia del robot; [D] di tma-ui-orcb 18:22Z, scelta (1)). La MANO resta `avv`: nel ventaglio
 * una carta `mini` passerebbe da 43,6 a ~34 px di passo, sotto i 44 di area di tocco, e non c'e` altezza da cedere per allargarla (condizione b della [D]). */
const taglie = (): Record<'plance' | 'avversario' | 'mano', TagliaCp> => (bassa() ? { plance: 'mini', avversario: 'mini', mano: 'avv' } : { plance: 'mia', avversario: 'mini', mano: 'avv' });

/** Le carte (e gli slot vuoti: sono loro il bersaglio della riga) della plancia `mini` restano disegnate a 28×40 ma l'area di tocco e` di 44 px d'altezza e arriva a meta` del vuoto fra una carta e la prossima (6 px in piu`, 3 per lato: a 5 px di vuoto il bordo esatto a meta` non verrebbe colpito da nessuna): un pseudo-elemento centrato che non sposta
 * nulla (come `TOCCO_44` del solitario, #863). Le file sono alte 44 (40 + il loro margine) e si toccano senza sovrapporsi. Solo sotto i 700 px. */
const TOCCO_PLANCIA_MINI =
  "[&_[data-fila]>div>*]:relative [&_[data-fila]>div>*]:after:absolute [&_[data-fila]>div>*]:after:left-1/2 [&_[data-fila]>div>*]:after:top-1/2 [&_[data-fila]>div>*]:after:h-[max(100%,2.75rem)] [&_[data-fila]>div>*]:after:w-[calc(100%+6px)] [&_[data-fila]>div>*]:after:-translate-x-1/2 [&_[data-fila]>div>*]:after:-translate-y-1/2 [&_[data-fila]>div>*]:after:content-['']";

/** Con al piu` tante carte in mano, sotto i 700 px, Conferma sta accanto al ventaglio (il passo di `avv` resta >= 44 px in 196 px di mano: 3 carte). */
const CARTE_CON_CONFERMA_ACCANTO = 3;

/** Il `data-fila` di `OfcRow` per ogni riga (le etichette del tavolo). */
const ETICHETTA: Record<Row, string> = { front: 'TOP', middle: 'MID', back: 'BOT' };
const FILE_TAVOLO: readonly Row[] = ['front', 'middle', 'back'];

/** Il bersaglio nel tavolo vero, dai suoi attributi `data-*`; `null` se non c'e` (niente accensione, mai un errore). */
function bersaglio(b: BersaglioCp, tavolo: HTMLElement): Element | null {
  switch (b.t) {
    case 'carta':
      return tavolo.querySelector(`[data-carta-mano="${chiave(b.carta)}"]`);
    case 'fila':
      return tavolo.querySelector(`[data-plancia-mia] [data-fila="${ETICHETTA[b.fila]}"]`);
    case 'conferma':
      return tavolo.querySelector('[data-azione="conferma"]');
    case 'totale':
      return tavolo.querySelector('[data-totale-mano]');
  }
}

/** Dentro il fantasma di «Mostramelo»: la carta che si muove (per una fila e` un riquadro vuoto, grande quanto l'origine). */
const rendiFantasma = (b: BersaglioCp) => (b.t === 'carta' ? <PlayingCard card={b.carta} /> : null);

/** Il tavolo vero con lo stato del copione. Un componente STABILE (a livello di modulo): lo strato non lo rimonta fra un giudizio e l'altro. */
function TavoloCp({ vista, onMossa, abilitato, evidenze }: PropsTavolo<StatoCp, MossaCp, BersaglioCp>) {
  const { t } = useTranslation('chinesepoker');
  const { plance: TAGLIA_PLANCE, avversario: TAGLIA_AVVERSARIO, mano: TAGLIA_MANO } = taglie();
  const mano = useMemo(() => [...vista.mano], [vista.mano]);
  const placement = useOfcPlacement(vista.giro, mano);
  const { clear, pending, selectedIndex } = placement;

  // il passo aspetta tante carte quante ne illumina in mano, e la conferma se illumina il tasto; un passo di sola lettura non ne aspetta
  const quota = evidenze.filter((e) => e.t === 'carta').length;
  const serveConferma = evidenze.some((e) => e.t === 'conferma');
  const mossa = useCallback((conferma: boolean): MossaCp => ({ posto: 'A', giro: vista.giro, piazza: pending.map((p) => ({ carta: p.card, fila: p.row })), ...(conferma ? { conferma } : {}) }), [vista.giro, pending]);

  // lo stato del copione e` cambiato (o il tutorial riparte): niente piazzamenti a meta`
  useEffect(() => {
    clear();
  }, [vista, clear]);
  // senza conferma (i passi 3 e 4) la mossa parte appena i piazzamenti sono tanti quanto il passo ne aspetta; giusta o no, le carte tornano in mano: il giudizio e` dello strato
  useEffect(() => {
    if (!abilitato || serveConferma || quota === 0 || pending.length !== quota) return;
    onMossa(mossa(false));
    clear();
  }, [abilitato, serveConferma, quota, pending, onMossa, mossa, clear]);
  const conferma = () => {
    onMossa(mossa(true));
    clear();
  };

  const posate = useMemo(() => new Set(pending.map((p) => chiave(p.card))), [pending]);
  const mioBoard = {
    front: [...vista.io.front, ...pending.filter((p) => p.row === 'front').map((p) => p.card)],
    middle: [...vista.io.middle, ...pending.filter((p) => p.row === 'middle').map((p) => p.card)],
    back: [...vista.io.back, ...pending.filter((p) => p.row === 'back').map((p) => p.card)],
  };
  const inMano = mano.filter((c) => !posate.has(chiave(c)));
  const conMossa = abilitato && quota > 0;
  const righeLibere = conMossa && selectedIndex !== null ? FILE_TAVOLO.filter((r) => mioBoard[r].length < CAPIENZA[r]) : [];
  const restaDaScartare = vista.giro > 0 && placement.discard !== null;

  const suRiga = (row: Row, index: number) => {
    if (!conMossa) return;
    if (selectedIndex !== null) {
      if (mioBoard[row].length < CAPIENZA[row]) placement.placeTo(row);
      return;
    }
    // una carta posata e non ancora giudicata: un tocco la rimette in mano
    const toccata: Card | undefined = mioBoard[row][index];
    if (toccata !== undefined && posate.has(chiave(toccata))) placement.unplace(toccata);
  };

  // il resoconto SENZA le due plance rivelate (`reveal: null`): in 360×640 non ci stanno col coach; la mia plancia sta sopra, in piccolo, e il conto sotto
  const robotHaGiocato = vista.robot.front.length + vista.robot.middle.length + vista.robot.back.length > 0;
  const resoconto = vista.resoconto === null ? null : resocontoDi({ ...vista.resoconto, reveal: null }, 'A');
  // la testata del tavolo (mano, round, punteggi) non c'e`: il tutorial non ha punteggi, e costa 23 px
  const testata = null;
  // Sotto i 700 px, con in mano al piu` 3 carte (passi 5, 6 e 8: quelli che nominano Conferma), il tasto sta NELLA RIGA del ventaglio invece che sotto: costa una riga di 44 px in meno ([D] di tma-ui-orcb 21:32Z, taglio 3b).
  // Con 4-5 carte il ventaglio, in 196 px, scenderebbe sotto i 44 px di passo (area di tocco), e Conferma resta sotto.
  const accanto = bassa() && inMano.length > 0 && inMano.length <= CARTE_CON_CONFERMA_ACCANTO;
  const trayDelTavolo = (
    <DrawTray
      cards={inMano}
      taglia={TAGLIA_MANO}
      selectedCard={selectedIndex === null ? null : (mano[selectedIndex] ?? null)}
      discarding={restaDaScartare}
      senzaArco
      onSelect={
        conMossa
          ? (card) => {
              const i = mano.findIndex((c) => chiave(c) === chiave(card));
              if (i !== -1) placement.select(i);
            }
          : undefined
      }
    />
  );
  const azioniDelTavolo = <CpAzioni modo="verticale" puoGiocare confermabile={abilitato && serveConferma && pending.length === quota} onConferma={conferma} onAbbandona={() => undefined} abbandonabile={false} evidenzia={restaDaScartare} />;
  const manoDelTavolo =
    inMano.length === 0 ? null : accanto ? (
      <div data-mano-e-conferma className="flex items-center gap-1">
        <div className="min-w-0 flex-1 [&_[data-scarto]>div:first-child]:sr-only">{trayDelTavolo}</div>
        <div className="w-[132px] shrink-0">{azioniDelTavolo}</div>
      </div>
    ) : (
      trayDelTavolo
    );
  const pezzi: PezziTavolo =
    resoconto !== null
      ? {
          testata,
          avversario: null,
          fascia: null,
          mio: null,
          mano: null,
          azioni: null,
          resoconto: (
            <>
              <div data-plancia-mia>
                <OfcBoard board={vista.io} taglia={TAGLIA_PLANCE} filoSulleLibere={false} />
              </div>
              <CpResoconto modo="verticale" r={resoconto} quattroColori={false} />
            </>
          ),
        }
      : {
          testata,
          avversario: (
            <div data-plancia-avv className="flex flex-col gap-0.5">
              {/* finche` l'avversario non ha giocato e` una striscia: solo l'etichetta (le sue file vuote costano 120 px che il coach non lascia) */}
              {!robotHaGiocato && <CpEtichettaPlancia nome={t('match.opponent')} dettaglio={t('match.discards', { n: vista.scarti.B })} />}
              {robotHaGiocato && <OfcBoard board={vista.robot} compact taglia={TAGLIA_AVVERSARIO} />}
            </div>
          ),
          fascia: null,
          mio: (
            <div data-plancia-mia className={cn('rounded-2xl transition-all', bassa() && TOCCO_PLANCIA_MINI, conMossa && 'ring-2 ring-brand shadow-[0_0_18px_1px_color-mix(in_srgb,var(--color-brand)_35%,transparent)]')}>
              <OfcBoard board={mioBoard} taglia={TAGLIA_PLANCE} filoSulleLibere={conMossa} pendingKeys={posate} onSlotTap={conMossa ? suRiga : undefined} highlightRows={righeLibere} />
            </div>
          ),
          mano: manoDelTavolo,
          azioni: accanto ? null : azioniDelTavolo,
          fissaInFondo: true,
        };
  return <CpTavoloLayout modo="verticale" pezzi={pezzi} compatto denso={bassa()} />;
}

const rendi = (p: PropsTavolo<StatoCp, MossaCp, BersaglioCp>) => <TavoloCp {...p} />;

/** Le righe che il riepilogo elenca (KB §1, «Cosa sa fare chi finisce»). */
const RIEPILOGO = ['righe', 'oro', 'ritmo', 'conto', 'bonus'] as const;

/**
 * Il LIVELLO del tutorial: a tutto schermo sopra la barra, aperto da `IconaTutorial` nella lobby `/chinesepoker`. Esce con la X, il tasto indietro di Telegram ed Escape.
 */
export function TutorialCp({ onChiudi }: { onChiudi: () => void }) {
  const { t } = useTranslation('chinesepoker');
  const testi = useMemo<TestiStrato>(
    () => ({
      titolo: t('guida.titolo'),
      passo: (n, tot) => t('guida.passo', { n, tot }),
      apertura: t('guida.apertura.sottotitolo'),
      riepilogo: t('guida.riepilogo.sottotitolo'),
      esci: t('guida.esci'),
      avanti: t('guida.avanti'),
      mostramelo: t('guida.mostramelo'),
      nonLi: t('guida.nonLi'),
      altra: t('guida.altra'),
      giusto: t('guida.giusto'),
      faiLaMossa: t('guida.faiLaMossa'),
      inizia: t('guida.inizia'),
      nonOra: t('guida.nonOra'),
      rivedi: t('guida.rivedi'),
      capito: t('guida.capito'),
    }),
    [t],
  );
  const testoPasso = useCallback(
    (id: string): TestiPasso => ({
      titolo: t(`guida.passi.${id}.titolo`),
      testo: t(`guida.passi.${id}.testo`),
      // il gesto dei passi 6 e 8 sta nel riquadro di stato (`come`), non nel testo: il coach e` piu` basso e il tavolo ha piu` zona ([D] di tma-ui-orcb 18:22Z, taglio 3a)
      come: t(`guida.passi.${id}.come`, { defaultValue: '' }) || undefined,
      altra: t(`guida.passi.${id}.altra`, { defaultValue: '' }) || undefined,
    }),
    [t],
  );
  const testoChiave = useCallback((k: string) => t(k), [t]);
  const apertura = useMemo<SchedaStrato>(() => ({ titolo: t('guida.apertura.titolo'), corpo: <p className="text-sm text-fg-muted">{t('guida.apertura.testo')}</p> }), [t]);
  const riepilogo = useMemo<SchedaStrato>(
    () => ({
      titolo: t('guida.riepilogo.titolo'),
      corpo: (
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg-muted">
          {RIEPILOGO.map((k) => (
            <li key={k}>{t(`guida.riepilogo.${k}`)}</li>
          ))}
        </ul>
      ),
    }),
    [t],
  );
  return (
    <StratoTutorial
      copione={COPIONE_CP}
      motore={MOTORE_CP}
      rendi={rendi}
      bersaglio={bersaglio}
      testi={testi}
      testoPasso={testoPasso}
      testoChiave={testoChiave}
      apertura={apertura}
      riepilogo={riepilogo}
      rendiFantasma={rendiFantasma}
      scheda="compatta"
      onChiudi={onChiudi}
    />
  );
}
